import React, { useEffect, useState } from 'react'
import { Button, Confirm, Dimmer, Embed, Form, Header, Image, Input, Loader, Segment } from 'semantic-ui-react'
import CoordinatesField from './fields/CoordinatesField'
import { WEBCAM_FILES_URL } from '../../server/constants'
import { loadTags } from '../actions/tags'
import { InputField, TextAreaField } from '@bsd/dashboard-toolkit/components/forms/fields'
import LocalizationField from './fields/LocalizationField'
import { DEFAULT_LOCALE, LOCALES } from '../constants'
import { sendNotification } from '@bsd/dashboard-toolkit/utils'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'


const CategoriesField = ({ label, placeholder, value, onChange }) => {
  const categories = useSelector(state => state.geoCategories)
  const [categoryOptions, setCategoryOptions] = useState([])
  useEffect(() => {
    setCategoryOptions(Object.values(categories).map(category => {
      return ({
        text: category.locales.EN.name,
        value: category.id,
      })
    }))
  }, [categories])
  return (
    <Form.Dropdown
      label={label}
      placeholder={placeholder}
      multiple
      clearable
      search
      selection
      additionLabel='Добавить тег '
      options={categoryOptions}
      value={value}
      onChange={(e, data) => {
        onChange(data.value)
      }}
    />
  )
}

const TagsField = ({ label, onChange, placeholder, value }) => {
  const [tagOptions, setTagOptions] = useState([])
  useEffect(() => {
    const selectedTagOptions = []
    for (const tag of value) {
      if (tagOptions.find(option => option.value === tag)) {
        continue
      }
      selectedTagOptions.push({ text: tag, value: tag })
    }
    setTagOptions([...tagOptions, ...selectedTagOptions])
  }, [value])

  const handleTagSearchChange = async (e, data) => {
    if (data.searchQuery) {
      const loadedTags = await loadTags({ search: data.searchQuery })
      const newTagOptions = []
      for (const tag of loadedTags) {
        if (tagOptions.find(option => option.value === tag._id)) {
          continue
        }
        newTagOptions.push({ text: tag._id, value: tag._id })
      }
      setTagOptions([...tagOptions, ...newTagOptions])
    }
  }

  return (
    <Form.Dropdown
      label={label}
      placeholder={placeholder}
      multiple
      clearable
      search
      onSearchChange={handleTagSearchChange}
      selection
      allowAdditions
      additionLabel='Добавить тег '
      onAddItem={(e, data) => {
        setTagOptions([...tagOptions, { text: data.value, value: data.value }])
      }}
      options={tagOptions}
      value={value}
      onChange={(e, data) => {
        onChange(data.value)
      }}
    />
  )
}

const ImageField = ({ camera, label, onChange }) => {
  const [cameraImage, setCameraImage] = useState(null)
  useEffect(() => {
    setCameraImage(
      camera._id && camera.image_url && WEBCAM_FILES_URL + `/cams/${camera._id}/img/pc_${camera.image_url}`
    )
  }, [camera])

  return (
    <Form.Field>
      <label>{label}</label>
      <Input type='file' name='image' accept='image/bmp,image/jpeg,image/png'
        onChange={e => {
          if (e.target.files && e.target.files[0]) {
            setCameraImage(URL.createObjectURL(e.target.files[0]))
            onChange(e)
          }
        }}/>
      {
        cameraImage &&
        <Segment><Image fluid src={cameraImage}/></Segment>
      }
    </Form.Field>
  )
}

const EmbedField = ({ camera, label, onChange }) => {
  const [cameraPreview, setCameraPreview] = useState(null)
  useEffect(() => {
    setCameraPreview(camera._id && camera.preview && WEBCAM_FILES_URL + `/cams/${camera._id}/${camera.preview}`)
  }, [camera])

  return (
    <Form.Field>
      <label>{label}</label>
      <Input type='file' name='image' accept="video/mp4,video/x-m4v,video/*"
        onChange={e => {
          if (e.target.files && e.target.files[0]) {
            setCameraPreview(URL.createObjectURL(e.target.files[0]))
            onChange(e)
          }
        }}/>
      {
        cameraPreview &&
        <Segment><Embed url={cameraPreview}/></Segment>
      }
    </Form.Field>
  )
}


const WebcamLocaleForm = ({ data, onChange }) =>
  <>
    <Form.Field>
      <label>Название</label>
      <InputField value={data.name} onChange={onChange('name')}/>
    </Form.Field>
    <Form.Field>
      <label>Описание</label>
      <TextAreaField value={data.description} onChange={onChange('description')} rows={5}/>
    </Form.Field>
    <Form.Field>
      <TagsField value={data.tags} label='Теги' placeholder='Теги' onChange={onChange('tags')}/>
    </Form.Field>
  </>

const WebcamForm = (props) => {
  const history = useHistory()
  const [camera, setCamera] = useState(null)
  const [newImage, setNewImage] = useState(null)
  const [newPreview, setNewPreview] = useState(null)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  useEffect(() => {
    let initialData
    if (!props.cameraId) {
      initialData = {
        camera_url: '',
        is_published: false,
        is_live: false,
        is_hd: false,
        is_paid: false,
        image_url: '',
        preview: '',
        locales: {},
        categories: [],
        utc_offset: 0,
      }
      if (props.geoCategoryId) {
        initialData.categories = [props.geoCategoryId]
      }
    } else {
      initialData = props.initialData
    }
    if (initialData) {
      const locales = { ...initialData.locales }
      LOCALES.forEach(locale => {
        if (!locales[locale.code]) {
          locales[locale.code] = { name: '', description: '', tags: [] }
        }
      })
      setCamera({ ...initialData, locales })
    }
  }, [props.initialData, props.cameraId])

  if (!camera) {
    return <Dimmer active inverted>
      <Loader/>
    </Dimmer>
  }

  const validate = () => {
    if (!camera.locales[DEFAULT_LOCALE].name) {
      sendNotification({ level: 'error', message: 'Заполните название' })
      return false
    }
    if (!camera.camera_url) {
      sendNotification({ level: 'error', message: 'Заполните URL камеры' })
      return false
    }
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (validate()) {
      const data = { camera: JSON.stringify(camera) }
      if (newPreview) {
        data['preview'] = newPreview
      }
      if (newImage) {
        data['image'] = newImage
      }
      props.onSubmit(data)
    }
  }

  return (
    <>
      <Header as='h3'>
        {props.cameraId ? 'Обновление' : 'Создание'} камеры
        <Button
          basic color='green' icon='save' size='mini'
          content='Сохранить'
          floated='right'
          onClick={handleSubmit}
        />
        {props.cameraId ?
          <Button
            basic color='red' icon='trash' size='mini' floated='right'
            content='Удалить камеру'
            onClick={() => setConfirmDeleteOpen(true)}
          /> : null}
        <Confirm
          cancelButton='Отмена'
          content='Вы уверены?'
          open={confirmDeleteOpen}
          onCancel={() => setConfirmDeleteOpen(false)}
          onConfirm={() => {
            setConfirmDeleteOpen(false)
            props.onDelete(props.cameraId)
            let historyPath = `/webcams`
            if (props.geoCategoryId) {
              historyPath = '/' + props.geoCategoryId + historyPath
            }
            history.push(historyPath)
          }}
        />
      </Header>

      <Form style={{ height: 'calc(100% - 58px)', overflowY: 'auto', paddingRight: '10px' }} onSubmit={handleSubmit}>
        <CoordinatesField value={camera.loc && { lon: camera.loc.coordinates[0], lat: camera.loc.coordinates[1] }}
          onChange={({ lat, lon }) => {
            setCamera(prevCamera => ({
              ...prevCamera, loc: {
                ...prevCamera.loc, type: 'Point', coordinates: [lon, lat],
              },
            }))
          }}/>
        <CategoriesField label='Категории' placeholder='Категории' value={camera.categories} onChange={value => {
          setCamera(prevCamera => (
            { ...prevCamera, categories: value }
          ))
        }}/>
        <Form.Input fluid label='Название (EN)' placeholder='Название (EN)' value={camera.locales.EN.name}
          onChange={(e, data) => {
            setCamera(prevCamera => (
              {
                ...prevCamera,
                locales: { ...prevCamera.locales, EN: { ...prevCamera.locales.EN, name: data.value } },
              }
            ))
          }}/>
        <Form.TextArea label='Описание (EN)' placeholder='Описание (EN)' value={camera.locales.EN.description} rows={5}
          onChange={(e, data) => {
            setCamera(prevCamera => (
              {
                ...prevCamera,
                locales: { ...prevCamera.locales, EN: { ...prevCamera.locales.EN, description: data.value } },
              }
            ))
          }}/>
        <Form.Input fluid label='URL камеры' placeholder='https://example.com/example.mp4' value={camera.camera_url}
          onChange={(e, data) => {
            setCamera(prevCamera => ({ ...prevCamera, camera_url: data.value }))
          }}/>
        <Form.Input fluid label='Часовой пояс' placeholder='7' value={camera.utc_offset} type="number"
          onChange={(e, data) => {
            setCamera(prevCamera => ({ ...prevCamera, utc_offset: data.value }))
          }}/>
        <Form.Group>
          <Form.Checkbox label='Публиковать' checked={camera.is_published} onChange={(e, data) => {
            setCamera(prevCamera => ({ ...prevCamera, is_published: data.checked }))
          }}/>
          <Form.Checkbox label='Прямая трансляция' checked={camera.is_live} onChange={(e, data) => {
            setCamera(prevCamera => ({ ...prevCamera, is_live: data.checked }))
          }}/>
          <Form.Checkbox label='HD' checked={camera.is_hd} onChange={(e, data) => {
            setCamera(prevCamera => ({ ...prevCamera, is_hd: data.checked }))
          }}/>
          <Form.Checkbox label='Платная' checked={camera.is_paid} onChange={(e, data) => {
            setCamera(prevCamera => ({ ...prevCamera, is_paid: data.checked }))
          }}/>
        </Form.Group>
        <TagsField locales={camera.locales} value={camera.locales.EN.tags} label='Теги (EN)' placeholder='Теги (EN)'
          onChange={value =>
            setCamera(prevCamera => (
              { ...prevCamera, locales: { ...prevCamera.locales, EN: { ...prevCamera.locales.EN, tags: value } } }
            ))}/>
        <Form.Group>
          <ImageField camera={camera} label='Картинка' onChange={e => {
            setNewImage(e.target.files[0])
          }}/>
          <EmbedField camera={camera} label='Превью' onChange={e => {
            setNewPreview(e.target.files[0])
          }}/>
        </Form.Group>
        <LocalizationField localeForm={WebcamLocaleForm} value={camera.locales} onChange={data => {
          setCamera(prevCamera => ({ ...prevCamera, locales: data }))
        }}/>

      </Form>
    </>
  )
}

export default WebcamForm
